import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ModalCloseButton from '../../../../Component/UI/ModalCloseButton';
import styled from 'styled-components';

export type DeliveryExcelFailure = { detail_seq: number | null; row?: number; message: string };

export default function DeliveryExcelGuide() {
  const [open, setOpen] = useState(false);
  return <>
    <ExcelButton type="button" aria-haspopup="dialog" onClick={() => setOpen(true)}>송장 엑셀 업로드 안내</ExcelButton>
    {open && <GuideDialog onClose={() => setOpen(false)} />}
  </>;
}

function GuideDialog({ onClose }: { onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const element = dialog.current!;
    const trigger = document.activeElement as HTMLElement;
    if (element.showModal) element.showModal(); else element.setAttribute('open', '');
    return () => { element.close?.(); trigger?.focus(); };
  }, []);
  return createPortal(<Dialog data-admin-modal="" ref={dialog} aria-labelledby="delivery-excel-guide-title"
    onCancel={event => { event.preventDefault(); onClose(); }}
    onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
    <GuideHeader data-admin-modal-header=""><h2 id="delivery-excel-guide-title">송장 엑셀 업로드 안내</h2><ModalCloseButton tone="admin" aria-label="송장 엑셀 안내 닫기" onClick={onClose} /></GuideHeader>
    <GuideContent data-admin-modal-body="" aria-label="송장 엑셀 작성 안내">
    <Flow><p><strong>발주서 다운 → 택배사·배송번호 입력 → 엑셀 업로드</strong> 순서로 진행해주세요.</p></Flow>
    <p>아래 택배사를 지원하며, 약칭이나 띄어쓰기가 달라도 같은 택배사로 자동 인식합니다.</p>
    <Carriers aria-label="자동 배송조회 지원 택배사">
      {['대한통운', 'CJ대한통운', '한진택배', '우체국택배', '롯데택배', '로젠택배'].map(name => <li key={name}>{name}</li>)}
    </Carriers>
    <details>
      <summary>필수 입력 규칙과 예시 보기</summary>
      <Rules>
        <div><dt>주문상품고유번호</dt><dd>발주서의 번호를 그대로 유지해주세요. 주문번호·상품코드와 다르며, 같은 번호가 중복되면 해당 행은 모두 실패합니다.</dd></div>
        <div><dt>택배사</dt><dd>20자 이내로 입력해주세요. ‘CJ’, ‘대한통운’, ‘한진’, ‘우체국’, ‘롯데’, ‘로젠’도 각각 정식 택배사명으로 연결됩니다.</dd></div>
        <div><dt>배송번호</dt><dd>송장번호를 30자 이내로 입력해주세요. 셀 서식을 <strong>텍스트</strong>로 바꾼 뒤 입력하면 앞자리 0이나 긴 번호가 바뀌는 것을 막을 수 있습니다.</dd></div>
      </Rules>
      <Example>
        <caption>입력 예시 · 주문상품고유번호는 실제 발주서의 값을 사용하세요.</caption>
        <thead><tr><th scope="col">주문상품고유번호</th><th scope="col">택배사</th><th scope="col">배송번호</th></tr></thead>
        <tbody><tr><td>2895513</td><td>CJ대한통운</td><td>001234567890</td></tr></tbody>
      </Example>
      <ul className="rules">
        <li>다운로드한 발주서의 첫 번째 시트를 사용하고, 첫 행의 열 이름을 변경하지 마세요. 파일 형식은 .xlsx 또는 .xls입니다.</li>
        <li>주문상품고유번호·택배사·배송번호는 모두 필수입니다. 빈칸이나 ‘-’는 등록할 수 없습니다.</li>
        <li>현재 방송의 발주완료 주문만 등록할 수 있습니다. 화면의 주문 선택 여부와 관계없이 파일에 포함된 행을 처리합니다.</li>
        <li>송장을 등록하면 발송 준비로 표시됩니다. 자동 배송조회 사용 시 집하가 확인되면 배송중으로 변경됩니다.</li>
      </ul>

    </details>
    </GuideContent>
    <GuideFooter data-admin-modal-footer=""><ExcelButton onClick={onClose}>확인</ExcelButton></GuideFooter>
  </Dialog>, document.body);
}

export function DeliveryExcelErrors({ failures }: { failures: DeliveryExcelFailure[] }) {
  const groups = new Map<string, DeliveryExcelFailure[]>();
  failures.forEach(failure => {
    const group = groups.get(failure.message) || [];
    group.push(failure);
    groups.set(failure.message, group);
  });

  return <ErrorPanel role="alert" aria-label="송장 업로드 실패 내역">
    <h3>수정이 필요한 항목 · {failures.length}건</h3>
    <p>아래 내용을 확인해 실패한 행을 수정한 뒤, 첫 행의 열 이름과 재시도할 행만 남겨 다시 업로드해주세요.</p>
    <ErrorList tabIndex={0} aria-label="실패 사유와 해당 주문상품">
      {Array.from(groups, ([message, items]) => <li key={message}>
        <strong>{message} <span>({items.length}건)</span></strong>
        <p>{items.map(failure => [failure.row ? `엑셀 ${failure.row}행` : '', failure.detail_seq ? `주문상품 ${failure.detail_seq}` : '주문상품고유번호 확인 필요'].filter(Boolean).join(' · ')).join(' / ')}</p>
      </li>)}
    </ErrorList>
  </ErrorPanel>;
}

const Panel = styled.section`
  margin: var(--space-4) 0;
  padding: var(--space-4);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  background: var(--color-bg-surface);
  color: var(--color-text-secondary);
  font-family: var(--font-family-base);
  font-size: var(--text-body);
  line-height: var(--leading-body);
  overflow-wrap: anywhere;
  h3 { margin: 0 0 var(--space-2); font-size: var(--text-body-lg); font-weight: var(--font-weight-semibold); color: var(--color-text-primary); }
  p { margin: var(--space-2) 0 0; }
  strong { font-weight: var(--font-weight-semibold); color: var(--color-text-primary); }
  details { margin-top: var(--space-3); border-top: 1px solid var(--color-border-default); }
  summary { padding: var(--space-3) 0; min-height: var(--touch-target); box-sizing: border-box; cursor: pointer; color: var(--color-text-link); }
  summary:focus-visible { outline: 2px solid var(--color-brand); outline-offset: 2px; border-radius: var(--radius-sm); }
  .rules { margin: var(--space-4) 0; padding-left: var(--space-5); }
  .rules li + li { margin-top: var(--space-2); }
`;
const Carriers = styled.ul`
  display: flex; flex-wrap: wrap; gap: var(--space-2);
  padding: 0; margin: var(--space-3) 0 0; list-style: none;
  li { padding: var(--space-1) var(--space-2); border: 1px solid var(--color-border-default); border-radius: var(--radius-sm); background: var(--color-bg-surface); color: var(--color-text-secondary); font-size: var(--text-caption); }
`;
const Rules = styled.dl`
  margin: 0 0 var(--space-5);
  display: grid; gap: 0;
  > div { display: grid; grid-template-columns: minmax(8rem, 1fr) minmax(0, 4fr); gap: var(--space-5); padding: var(--space-4) 0; border-bottom: 1px solid var(--color-border-default); }
  dt { color: var(--color-text-primary); font-weight: var(--font-weight-semibold); }
  dd { margin: 0; line-height: var(--leading-body); }
  @media (max-width: 640px) { > div { grid-template-columns: minmax(0, 1fr); gap: var(--space-2); } }
`;
const Example = styled.table`
  width: 100%; border-collapse: collapse; table-layout: fixed; color: var(--color-text-primary);
  caption { margin-bottom: var(--space-2); text-align: left; font-size: var(--text-caption); color: var(--color-text-secondary); }
  th, td { padding: var(--space-2); border: 1px solid var(--color-border-table); text-align: left; }
  th { background: var(--color-bg-muted); font-weight: var(--font-weight-semibold); }
  td { font-variant-numeric: tabular-nums; }
`;
const ErrorPanel = styled(Panel)`
  border-color: var(--color-border-danger);
  h3 { color: var(--color-danger-strong); }
`;
const ErrorList = styled.ul`
  max-height: 18rem; overflow-y: auto;
  margin: var(--space-3) 0 0; padding: 0; list-style: none;
  li { padding: var(--space-3) 0; border-top: 1px solid var(--color-border-default); }
  li:last-child { padding-bottom: 0; }
  p { font-variant-numeric: tabular-nums; }
  &:focus-visible { outline: 2px solid var(--color-brand); outline-offset: 2px; }
`;

export const ExcelButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex; align-items: center; justify-content: center;
  box-sizing: border-box; height: var(--control-lg); min-height: var(--control-lg);
  margin: 0; padding: 0 var(--space-5); flex-shrink: 0;
  border: 1px solid var(--color-border-default); border-radius: var(--radius-md);
  background: var(--color-bg-surface); color: var(--color-text-primary);
  font-family: var(--font-family-base); font-size: var(--text-body-lg); font-weight: var(--font-weight-medium);
  white-space: nowrap; cursor: pointer;
  &:hover { background: var(--color-bg-subtle); }
  &:focus-visible { outline: 2px solid var(--color-brand); outline-offset: 2px; }
`;
const Dialog = styled.dialog`
  :root[data-admin-design] && { inline-size: min(860px, calc(100vw - var(--space-8))); max-inline-size: 860px; max-height: calc(100dvh - var(--space-8)); }
  box-sizing: border-box; width: calc(100% - var(--space-8)); max-width: 860px;
  max-height: calc(100dvh - var(--space-8)); padding: 0;
  border: 1px solid var(--color-border-default); border-radius: var(--radius-lg);
  background: var(--color-bg-elevated); color: var(--color-text-primary); box-shadow: var(--shadow-lg);
  overflow: hidden; overscroll-behavior: contain;
  &[open] { display: flex; flex-direction: column; }
  &::backdrop { background: var(--color-bg-overlay); }
`;
const GuideContent = styled(Panel)`
  flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain;
  margin: 0; padding: var(--space-5) var(--space-6); border: 0; background: transparent;
  details { margin-top: var(--space-5); }
  summary { font-weight: var(--font-weight-medium); }
  .rules { padding: var(--space-4) var(--space-5) var(--space-4) var(--space-8); background: var(--color-bg-surface); border-radius: var(--radius-md); }
  @media (max-width: 640px) { padding: var(--space-4); }
`;
const GuideHeader = styled.header`
  display: flex; flex: 0 0 auto; align-items: center; justify-content: space-between; gap: var(--space-4);
  padding: var(--space-4) var(--space-6); border-bottom: 1px solid var(--color-border-default);
  h2 { margin: 0; font-size: var(--text-body-lg); font-weight: var(--font-weight-semibold); }
  @media (max-width: 640px) { padding: var(--space-4); }
`;
const GuideFooter = styled.footer`
  flex: 0 0 auto; display: flex; justify-content: flex-end; padding: var(--space-3) var(--space-6); border-top: 1px solid var(--color-border-default);
  button { min-width: 6rem; background: var(--color-brand); color: var(--color-text-on-brand); border-color: var(--color-brand); font-size: var(--text-body); }
  button:hover { background: var(--color-brand-hover); }
`;
const Flow = styled.div`
  padding-bottom: var(--space-4); margin-bottom: var(--space-4); border-bottom: 1px solid var(--color-border-default);
  p { margin: 0; line-height: var(--leading-body); }
`;
